import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import { ALARM_PAGE } from "../../../shared/constants/Constants";
import ListView from "../../../shared/components/listview/ListView";
import DefaultButton from "../../../shared/components/DefaultButton";
import { loadAlarmPageDataRequest, loadAlarmPageUpdateDataRequest, resetAlarmPageUpdateData } from "../redux/Actions";
import Modalpopup from "../../../shared/components/Modalpopup";

const AlarmPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { alarmPageList, updateSuccess } = useSelector((store) => store.alarmPage);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [query] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    const urlStatus = query.get('status');
    if (urlStatus) setStatusFilter(urlStatus);
  }, [query]);

  useEffect(() => {
    dispatch(loadAlarmPageDataRequest());
  }, [dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(loadAlarmPageDataRequest());
      dispatch(resetAlarmPageUpdateData());
      setSelectedRows([]);
    }
  }, [updateSuccess, dispatch]);

  const rowData = useMemo(() => {
    const list = alarmPageList?.data ?? alarmPageList;
    if (!list?.length) return [];

    let filteredList = list;
    if (statusFilter && statusFilter !== 'all') {
      filteredList = list.filter(item => item.status === statusFilter);
    }

    return filteredList.map((item, index) => ({
      id: index,
      submission_id: item.submission_id,
      emp_name: item.created_emp,
      employees: item.employees || [],
      sap_id: item.sap_id,
      store_name: item.store_name,
      created_emp_email: item.created_emp_email,
      created_at: new Date(item.created_at).toLocaleString("en-US", {
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      }),
      created_at_export: (() => {
        const d = new Date(item.created_at);
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
      })(),
      status: item.status,
      can_edit_delete: item.status !== "completed",
    }));
  }, [alarmPageList, statusFilter]);

  const handleMarkCompleted = () => {
    const pendingSubmissions = [
      ...new Map(
        selectedRows
          .filter((row) => row.status === "pending")
          .map((row) => [row.submission_id, { submission_id: row.submission_id, status: "completed" }])
      ).values(),
    ];
    if (!pendingSubmissions.length) return;
    pendingSubmissions.forEach((payload) => {
      dispatch(loadAlarmPageUpdateDataRequest(payload));
    });
  };

  const pendingCount = selectedRows.filter((r) => r.status === "pending").length;

  const columnDefs = [
    { display_name: "Submission ID", field: "submission_id", type: "number", hide: false },
    {
      display_name: "Employee Name",
      field: "emp_name",
      type: "customLink",
      functionTo: (row) => {
        if (row.employees && row.employees.length > 0) handleShowEmployees(row);
      },
    },
    { display_name: "Store",      field: "store_name", type: "text", width: 80 },
    { display_name: "Created At", field: "created_at", type: "date", width: 180 },
    { display_name: "Status",     field: "status",     type: "dropdown", width: 80, dropdownOptions: ["pending", "completed"] },
    { field: "actions",           display_name: "Actions", type: "actions", width: 80 },
  ];

  const handleShowEmployees = (row) => {
    setSelectedRowData(row);
    setShowEmployeeModal(true);
  };

  const handleEdit = (row) => {
    setEditRow(row);
    setShowEditModal(true);
  };

  const handleCustomExport = (selectedIds) => {
    const selected = rowData.filter(row => selectedIds.includes(row.id));

    const headers = [
      "Submission ID",
      "SAP Code",
      "Store Name",
      "Submitted By",
      "Submitter Email",
      "Date",
      "Status",
      "Employee Name",
      "Employee ID",
      "Job Title",
      "Phone Number",
    ];

    const rows = [];
    selected.forEach(row => {
      const sapCode   = row.store_name?.split(",").pop()?.trim()  || "";
      const storeName = row.store_name?.split(",")[0]?.trim()     || "";

      if (row.employees && row.employees.length > 0) {
        row.employees.forEach(emp => {
          rows.push([
            row.submission_id,
            sapCode,
            storeName,
            row.emp_name?.split(",")[0]?.trim() || "",
            row.created_emp_email,
            row.created_at_export,
            row.status.toUpperCase(),
            emp.emp_name?.split(",")[0]?.trim() || "",
            emp.emp_no    || "",
            emp.job_title || "",
            emp.phone     || "",
          ]);
        });
      } else {
        rows.push([
          row.submission_id,
          row.sap_id,
          row.store_name,
          row.emp_name?.split(",")[0]?.trim() || "",
          row.created_emp_email,
          row.created_at_export,
          row.status.toUpperCase(),
          "", "", "", "",
        ]);
      }
    });

    const csvContent = [headers, ...rows]
      .map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alarm_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs children={[{ href: "/alarmpage/", label: ALARM_PAGE }]} />
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Body>
          <ListView
            columnNames={columnDefs}
            rowData={rowData}
            onRowSelectionChanged={setSelectedRows}
            clickEdit={handleEdit}
            customExport={handleCustomExport}
            additionalButton={
              <DefaultButton
                buttonClass="default-btn-color mb-1 mx-1 py-1"
                buttonTitle={`Update Status (${pendingCount})`}
                actionBlock={handleMarkCompleted}
                disabled={pendingCount === 0}
              />
            }
          />
        </Card.Body>
      </Card>

      <Modalpopup height="auto" size="md" show={showEditModal} handleClose={() => setShowEditModal(false)} actionTitle="Update Status">
        {editRow && (
          <>
            {editRow.status === "pending" ? (
              <>
                <p>
                  Update status of SAP ID <strong>{editRow.sap_id}</strong> to <strong>completed</strong>?
                </p>
                <div className="mt-3">
                  <DefaultButton buttonClass="btn btn-secondary me-2" buttonTitle="Cancel" actionBlock={() => setShowEditModal(false)} />
                  <DefaultButton
                    buttonClass="default-btn-color"
                    buttonTitle="Update"
                    actionBlock={() => {
                      dispatch(loadAlarmPageUpdateDataRequest({ submission_id: editRow.submission_id, status: "completed" }));
                      setShowEditModal(false);
                      setEditRow(null);
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <p>
                  This record with SAP ID <strong>{editRow.sap_id}</strong> is already <strong>{editRow.status}</strong>.
                </p>
                <div className="mt-3">
                  <DefaultButton buttonClass="btn btn-secondary" buttonTitle="Close" actionBlock={() => setShowEditModal(false)} />
                </div>
              </>
            )}
          </>
        )}
      </Modalpopup>

      {/* FIX 2: Employee modal shows only Employee Name (no Employee ID column) */}
      <Modalpopup height="auto" size="lg" show={showEmployeeModal} handleClose={() => setShowEmployeeModal(false)} actionTitle="Employee Details">
        {selectedRowData ? (
          <>
            <div className="mb-3">
              <strong>Store</strong> {selectedRowData.store_name}
            </div>
            {selectedRowData.employees && selectedRowData.employees.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead style={{ backgroundColor: "#888888", color: "#ffffff" }}>
                    <tr>
                      <th>Employee Name</th>
                      <th>Job Title</th>
                      <th>Phone Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRowData.employees.map((emp, index) => (
                      <tr key={index}>
                        <td>{emp.emp_name    || ""}</td>
                        <td>{emp.job_title   || ""}</td>
                        <td>{emp.phone || emp.phone_number || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No employee data available.</p>
            )}
          </>
        ) : (
          <p>No data available.</p>
        )}
      </Modalpopup>
    </>
  );
};

export default AlarmPage;